import React, { useState, useContext } from 'react';
import { Card, Row, Col, Typography, Button, message, Tabs, Tooltip } from 'antd';
import { Line, Column } from '@ant-design/plots';
import { BarChartOutlined, AppstoreOutlined } from '@ant-design/icons';
import { GlobalContext } from '../../App';
import AOIHistoryService from '../../services/AOIHistory';

const { Title } = Typography;

const normalizeClassificationLabel = (rawLabel) => {
  if (!rawLabel || typeof rawLabel !== 'string') {
    return 'Unknown';
  }

  const classMatch = rawLabel.match(/Class\s+(\d+)/i);
  const originalMatch = rawLabel.match(/from original:\s*([^)]+)/i);

  if (classMatch) {
    return `Class ${classMatch[1]}`;
  }

  if (originalMatch) {
    return originalMatch[1].trim();
  }

  return rawLabel.replace(/_/g, ' ');
};

const normalizeClassificationResults = (results) => {
  if (!Array.isArray(results)) {
    return [];
  }

  const bestResultsByPeriod = new Map();

  results.forEach((result) => {
    if (!Array.isArray(result) || result.length < 5) {
      return;
    }

    const [geometry, startDate, endDate, rawLabel, confidence] = result;
    const periodKey = `${startDate}-${endDate}`;
    const normalizedResult = {
      geometry,
      startDate,
      endDate,
      cropType: normalizeClassificationLabel(rawLabel),
      confidence: Number(confidence) || 0,
    };

    const existingResult = bestResultsByPeriod.get(periodKey);
    if (!existingResult || normalizedResult.confidence > existingResult.confidence) {
      bestResultsByPeriod.set(periodKey, normalizedResult);
    }
  });

  return Array.from(bestResultsByPeriod.values()).sort((a, b) => {
    return new Date(a.startDate) - new Date(b.startDate);
  });
};

const TimeseriesCharts = ({ data, polygon }) => {
  console.log('TimeseriesCharts received data:', data);
  const { investigatePolygon } = useContext(GlobalContext);
  const [zoomRange, setZoomRange] = useState(null);
  const [chartInstances, setChartInstances] = useState({});
  const [classificationResults, setClassificationResults] = useState([]);
  const [isClassifying, setIsClassifying] = useState(false);
  const activePolygon = polygon || investigatePolygon;
  
  // Handle nested data structure
  const actualData = data?.data || data;
  const normalizedClassificationResults = normalizeClassificationResults(classificationResults);
  const hasClassificationResults = normalizedClassificationResults.length > 0;
  
  if (!actualData || !actualData.band_list || !actualData.ts) {
    console.log('No data or missing band_list/ts');
    return <div style={{ padding: '20px' }}><Title level={3}>No data available</Title></div>;
  }

  const { band_list, ts } = actualData;
  console.log('band_list:', band_list);
  console.log('ts:', ts);

  const handleAnalyzeHistory = async () => {
    if (!activePolygon || !actualData) {
      message.error('No polygon or history data available');
      return;
    }

    setIsClassifying(true);
    try {
      const results = await AOIHistoryService.classifyAOIHistory(
        activePolygon.geometry,
        actualData
      );
      setClassificationResults(Array.isArray(results) ? results : []);
      message.success('AOI history analyzed successfully');
    } catch (error) {
      message.error('Failed to analyze AOI history');
      console.error('Classification error:', error);
    } finally {
      setIsClassifying(false);
    }
  };

  const getCropIcon = (cropType) => {
    const icons = {
      'wheat': '🌾',
      'corn': '🌾',
      '2nd_corn': '🌾',
      'spinach': '🌱',
      'beans': '🌱',
      'peas': '🌱',
      'clover': '☘️',
      'sunflower': '🌻',
      'strawberry': '🍓',
      'cotton': '🌿',
      '2nd_cotton': '🌿'
    };
    return icons[cropType?.toLowerCase?.()] || '🌱';
  };

  const groupResultsByYear = (results) => {
    const grouped = {};
    results.forEach(result => {
      const year = new Date(result.startDate).getFullYear();
      if (!grouped[year]) grouped[year] = [];
      grouped[year].push(result);
    });
    return grouped;
  };

  const getFieldMetrics = () => {
    if (!normalizedClassificationResults.length) {
      return {
        analyzedYears: 0,
        topClass: 'N/A',
        totalResults: 0,
        avgConfidence: 0,
      };
    }

    const years = new Set();
    const classCounts = {};
    let confidenceSum = 0;

    normalizedClassificationResults.forEach((result) => {
      years.add(new Date(result.startDate).getFullYear());
      classCounts[result.cropType] = (classCounts[result.cropType] || 0) + 1;
      confidenceSum += result.confidence;
    });

    const topClass = Object.entries(classCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || 'N/A';

    return {
      analyzedYears: years.size,
      topClass,
      totalResults: normalizedClassificationResults.length,
      avgConfidence: Math.round(confidenceSum / normalizedClassificationResults.length),
    };
  };

  const getCropNDVIData = (crop, year) => {
    const yearData = ts[year];
    if (!yearData || !yearData.NDVI || !yearData.dates) return [];
    
    const startDate = new Date(crop.startDate);
    const endDate = new Date(crop.endDate);
    
    const filteredData = [];
    yearData.dates.forEach((date, index) => {
      const currentDate = new Date(date);
      if (currentDate >= startDate && currentDate <= endDate && yearData.NDVI[index] !== null) {
        filteredData.push({
          date: date,
          value: yearData.NDVI[index],
          isCloudy: yearData.NDVI[index] < 0.2 // Mock cloudy day detection
        });
      }
    });
    
    return filteredData.sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  const renderCropChart = (crop, year) => {
    const chartData = getCropNDVIData(crop, year);
    if (chartData.length === 0) {
      return <div style={{ padding: '20px', textAlign: 'center' }}>No NDVI data available</div>;
    }

    const config = {
      data: chartData,
      height: 200,
      width: 400,
      xField: 'date',
      yField: 'value',
      smooth: true,
      point: {
        size: 2,
        shape: (datum) => datum.isCloudy ? 'triangle' : 'circle',
        style: (datum) => ({
          fill: datum.isCloudy ? '#ff4d4f' : '#1890ff',
          stroke: datum.isCloudy ? '#ff4d4f' : '#1890ff'
        })
      },
      line: {
        color: '#52c41a',
        size: 1
      },
      tooltip: {
        formatter: (datum) => ({
          name: 'NDVI',
          value: datum.value?.toFixed(3),
          ...(datum.isCloudy && { title: 'Cloudy Day' })
        })
      },
      yAxis: {
        title: {
          text: 'NDVI Value',
          style: { fontSize: 12 }
        }
      },
      xAxis: {
        title: {
          text: 'Date',
          style: { fontSize: 12 }
        }
      },
      animation: false
    };

    return (
      <div style={{ 
        padding: '16px', 
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)', 
        borderRadius: '12px', 
        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        border: '1px solid rgba(255,255,255,0.8)'
      }}>
        <div style={{ marginBottom: '8px', textAlign: 'center' }}>
          <Typography.Text strong style={{ color: '#1890ff' }}>
            {getCropIcon(crop.cropType)} {crop.cropType.toUpperCase()} NDVI
          </Typography.Text>
          <br />
          <Typography.Text type="secondary" style={{ fontSize: '11px' }}>
            {new Date(crop.startDate).toLocaleDateString()} - {new Date(crop.endDate).toLocaleDateString()}
          </Typography.Text>
        </div>
        <Line {...config} />
      </div>
    );
  };

  // Transform data for charts
  const getChartData = (band) => {
    const chartData = [];
    console.log('Processing band:', band);
    
    Object.keys(ts).forEach(year => {
      const yearData = ts[year];
      const bandKey = yearData[band] ? band : band.toUpperCase();
      
      if (yearData[bandKey] && yearData.dates) {
        yearData[bandKey].forEach((value, index) => {
          if (yearData.dates[index]) {
            chartData.push({
              date: yearData.dates[index],
              value: value,
              year: year
            });
          }
        });
      }
    });

    return chartData.sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  // Transform SCL histogram data
  const getSCLHistogramData = () => {
    const histogramData = [];
    
    Object.keys(ts).forEach(year => {
      const yearData = ts[year];
      if (yearData.SCL && yearData.dates) {
        yearData.SCL.forEach((sclObj, index) => {
          if (yearData.dates[index] && sclObj) {
            Object.entries(sclObj).forEach(([classId, count]) => {
              histogramData.push({
                date: yearData.dates[index],
                class: `Class ${classId}`,
                count: count,
                year: year
              });
            });
          }
        });
      }
    });

    return histogramData;
  };

  const getConfig = (band) => ({
    height: 200,
    xField: 'date',
    yField: 'value',
    seriesField: 'year',
    smooth: true,
    animation: false,
    point: {
      size: 3,
      shape: 'circle',
    },
    legend: {
      position: 'top',
    },
    tooltip: {
      formatter: (datum) => {
        return {
          name: `Year ${datum.year}`,
          value: datum.value?.toFixed(4) || 'N/A',
        };
      },
    },
    interactions: [
      {
        type: 'brush-x',
        enable: true,
      },
    ],
    onReady: (plot) => {
      setChartInstances(prev => ({ ...prev, [band]: plot }));
      plot.on('brush-x:end', (evt) => {
        const { xValues } = evt;
        if (xValues && xValues.length === 2) {
          const newZoomRange = [xValues[0], xValues[1]];
          setZoomRange(newZoomRange);
          
          // Update all other charts
          Object.entries(chartInstances).forEach(([chartBand, chartPlot]) => {
            if (chartBand !== band && chartPlot) {
              chartPlot.updateConfig({
                scale: {
                  x: {
                    domain: newZoomRange,
                  },
                },
              });
              chartPlot.render();
            }
          });
        }
      });
    },
    ...(zoomRange && {
      scale: {
        x: {
          domain: zoomRange,
        },
      },
    }),
  });

  const tabItems = [
    {
      key: 'bands',
      label: (
        <span>
          <BarChartOutlined />
          Band Analysis
        </span>
      ),
      children: (
        <div style={{ padding: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <Typography.Text type="secondary">5-Year Satellite Band History</Typography.Text>
            {zoomRange && (
              <Button 
                size="small"
                onClick={() => {
                  setZoomRange(null);
                  Object.values(chartInstances).forEach(plot => {
                    if (plot) {
                      plot.updateConfig({
                        scale: { x: { domain: undefined } }
                      });
                      plot.render();
                    }
                  });
                }}
              >
                Reset Zoom
              </Button>
            )}
          </div>
          <Row gutter={[12, 12]}>
            {band_list.map((band) => {
              if (band === 'SCL') {
                const histogramData = getSCLHistogramData();
                if (histogramData.length === 0) return null;
                return (
                  <Col span={12} key={band}>
                    <Card 
                      title="Scene Classification (SCL)" 
                      size="small"
                      style={{ 
                        borderRadius: '8px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                      }}
                    >
                      <Column 
                        data={histogramData}
                        height={200}
                        xField="date"
                        yField="count"
                        seriesField="class"
                        isStack={true}
                        animation={false}
                        legend={{ position: 'top' }}
                        tooltip={{
                          formatter: (datum) => ({
                            name: datum.class,
                            value: Math.round(datum.count)
                          })
                        }}
                      />
                    </Card>
                  </Col>
                );
              } else {
                const chartData = getChartData(band.toLowerCase());
                if (chartData.length === 0) return null;
                return (
                  <Col span={12} key={band}>
                    <Card 
                      title={band === 'CLOUD' ? 'Cloud Coverage' : `Band ${band}`} 
                      size="small"
                      style={{ 
                        borderRadius: '8px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                      }}
                    >
                      <Line {...getConfig(band)} data={chartData} />
                    </Card>
                  </Col>
                );
              }
            })}
          </Row>
        </div>
      )
    },
    {
      key: 'classification',
      label: (
        <span>
          <AppstoreOutlined />
          Classification Results
          {hasClassificationResults && (
            <span style={{ 
              marginLeft: '8px', 
              background: '#1890ff', 
              color: 'white', 
              borderRadius: '10px', 
              padding: '2px 6px', 
              fontSize: '10px' 
            }}>
              {normalizedClassificationResults.length}
            </span>
          )}
        </span>
      ),
      children: hasClassificationResults ? (
        <div style={{ padding: '24px' }}>
          <Row gutter={24} style={{ marginBottom: '32px' }}>
            <Col span={24}>
              <Card style={{ height: '200px', borderRadius: '12px' }}>
                <Typography.Title level={5} style={{ marginBottom: '16px', color: '#1890ff' }}>
                  Classification Summary
                </Typography.Title>
                {(() => {
                  const metrics = getFieldMetrics();
                  return (
                    <Row gutter={[16, 16]}>
                      <Col span={12}>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#52c41a' }}>
                            {metrics.analyzedYears}
                          </div>
                          <Typography.Text type="secondary">Analysed Years</Typography.Text>
                        </div>
                      </Col>
                      <Col span={12}>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1890ff' }}>
                            {metrics.topClass}
                          </div>
                          <Typography.Text type="secondary">Top Predicted Class</Typography.Text>
                        </div>
                      </Col>
                      <Col span={12}>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#722ed1' }}>
                            {metrics.totalResults}
                          </div>
                          <Typography.Text type="secondary">Classified Periods</Typography.Text>
                        </div>
                      </Col>
                      <Col span={12}>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#fa8c16' }}>
                            {metrics.avgConfidence}%
                          </div>
                          <Typography.Text type="secondary">Avg Confidence</Typography.Text>
                        </div>
                      </Col>
                    </Row>
                  );
                })()}
              </Card>
            </Col>
          </Row>

          {/* Horizontal Timeline */}
          <div style={{ marginBottom: '16px' }}>
            <Typography.Title level={5} style={{ color: '#1890ff' }}>Classification Timeline</Typography.Title>
            <Typography.Text type="secondary">Top classification per analysed period from the AOI service</Typography.Text>
          </div>
          
          {(() => {
            const groupedResults = groupResultsByYear(normalizedClassificationResults);
            const years = Object.keys(groupedResults).sort();
            
            return (
              <div style={{ 
                display: 'flex', 
                overflowX: 'auto', 
                padding: '20px 0',
                gap: '40px'
              }}>
                {years.map((year, yearIndex) => (
                  <div key={year} style={{ 
                    minWidth: '200px',
                    position: 'relative'
                  }}>
                    {/* Timeline connector */}
                    {yearIndex < years.length - 1 && (
                      <div style={{
                        position: 'absolute',
                        top: '20px',
                        right: '-40px',
                        width: '40px',
                        height: '2px',
                        background: '#d9d9d9',
                        zIndex: 1
                      }} />
                    )}
                    
                    {/* Year node */}
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center'
                    }}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        background: '#1890ff',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold',
                        marginBottom: '16px',
                        boxShadow: '0 4px 12px rgba(24, 144, 255, 0.3)',
                        zIndex: 2,
                        position: 'relative'
                      }}>
                        {year}
                      </div>
                      
                      {/* Crops for this year */}
                      <div style={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        gap: '8px',
                        alignItems: 'center'
                      }}>
                        {groupedResults[year].map((crop, cropIndex) => (
                          <Tooltip
                            key={cropIndex}
                            title={renderCropChart(crop, year)}
                            placement="top"
                            overlayStyle={{ 
                              maxWidth: 'none',
                              border: 'none',
                              borderRadius: '12px',
                              boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                              padding: 0
                            }}
                            overlayInnerStyle={{
                              background: 'transparent',
                              border: 'none',
                              borderRadius: '12px',
                              padding: 0
                            }}
                            mouseEnterDelay={0}
                            mouseLeaveDelay={0.1}
                          >
                            <div style={{
                              background: 'white',
                              border: `2px solid ${crop.confidence > 70 ? '#52c41a' : crop.confidence > 50 ? '#faad14' : '#ff4d4f'}`,
                              borderRadius: '20px',
                              padding: '6px 12px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                              minWidth: '120px',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease'
                            }}>
                              <span style={{ fontSize: '16px' }}>
                                {getCropIcon(crop.cropType)}
                              </span>
                              <div style={{ textAlign: 'center' }}>
                                <div style={{ 
                                  fontSize: '11px', 
                                  fontWeight: 'bold',
                                  color: '#1890ff'
                                }}>
                                  {crop.cropType.toUpperCase()}
                                </div>
                                <div style={{ 
                                  fontSize: '10px', 
                                  color: crop.confidence > 70 ? '#52c41a' : crop.confidence > 50 ? '#faad14' : '#ff4d4f'
                                }}>
                                  {crop.confidence}%
                                </div>
                              </div>
                            </div>
                          </Tooltip>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      ) : (
        <div style={{ 
          padding: '40px',
          margin: '24px', 
          textAlign: 'center',
          //background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '12px',
          color: 'white'
        }}>
          <AppstoreOutlined style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.8 }} />
          <Typography.Title level={4} style={{ color: '#667eea', marginBottom: '8px' }}>
            No Classification Results Yet
          </Typography.Title>
          <Typography.Text style={{ color: '#667eea' }}>
            Click "Analyze AOI History" to run AOI classification on the loaded history
          </Typography.Text>
        </div>
      )
    }
  ];

  return (
    <div style={{ 
      padding: '12px',
      background: 'transparent',
      minHeight: '100%',
      maxHeight: '35vh',
      overflow: 'auto'
    }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '12px',
        background: 'rgba(255, 255, 255, 0.9)',
        padding: '12px',
        borderRadius: '12px',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.2)'
      }}>
        <div>
          <Title level={4} style={{ margin: 0, color: '#1890ff' }}>
            AOI Analysis Dashboard
          </Title>
          <Typography.Text type="secondary">Satellite data insights and AOI classification</Typography.Text>
        </div>
        <Button 
          type="primary"
          size="large"
          loading={isClassifying}
          onClick={handleAnalyzeHistory}
          disabled={!activePolygon || !actualData}
          style={{
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(24, 144, 255, 0.3)'
          }}
        >
          {isClassifying ? 'Analyzing...' : 'Analyze AOI History'}
        </Button>
      </div>
      
      <div style={{
        background: 'rgba(255, 255, 255, 0.9)',
        borderRadius: '12px',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        overflow: 'hidden'
      }}>
        <Tabs 
          items={tabItems}
          defaultActiveKey="bands"
          size="large"
          style={{ margin: 0 }}
          tabBarStyle={{
            background: '#fafafa',
            margin: 0,
            paddingLeft: '16px'
          }}
        />
      </div>
    </div>
  );
};

export default TimeseriesCharts;
